import { WidgetProps } from '@features/dashboard/types';
import { MenuItem, Select } from '@mui/material';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import Widget, { Content, Header } from '../Widget';
import Settings from '../settings/Settings';
import TradingProvider from './TradingContext';
import { TradingPanelComponent } from './components/TradingPanelComponent';
import { useTradingPermissions } from './hooks/useTradingPermissions';
import { TradingV2WidgetPayload } from './types';
import { useWidgetLifecycleTelemetry } from '../useWidgetLifecycleTelemetry';

type TradingV2Props = WidgetProps & {
  payload?: TradingV2WidgetPayload;
  isFrozen?: boolean;
};

export const TradingV2 = ({ payload, isFrozen, ...otherProps }: TradingV2Props) => {
  const { id, title } = otherProps;

  const user = useUserContext();
  const { tradingAccounts, selectedTradingAccount, setSelectedTradingAccount, userTradeLimits, defaultTradeSizes } = useTradingPermissions(
    user,
    payload?.selectedTradingAccount
  );
  useWidgetLifecycleTelemetry(id, title, 'TradingV2', payload);

  if (!payload) return null;
  if (userTradeLimits.length === 0) return null;

  return (
    <Widget {...otherProps}>
      <TradingProvider isFrozen={isFrozen}>
        <Header title={title}>
          {selectedTradingAccount ? (
            <Select
              type=""
              size="small"
              autoWidth
              sx={{
                fontSize: 11,
                height: 20,
                width: '100%',
                borderRadius: 0,
                '& .MuiSelect-select': {
                  padding: 0,
                  paddingLeft: 0.5,
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                  padding: 0,
                },
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 100, width: 'fit-content' } } }}
              value={selectedTradingAccount}
              onChange={(event: any) => setSelectedTradingAccount(event.target.value)}
              tabIndex={-1}
            >
              {tradingAccounts.map((account: string) => (
                <MenuItem key={account} value={account} sx={{ fontSize: 11, padding: 0, paddingLeft: 1.5, paddingRight: 1.5 }}>
                  {account.toLocaleUpperCase()}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <></>
          )}
          <Settings id={`trading-settings-${id}`} title={title} widgetId={id} />
        </Header>
        {selectedTradingAccount && (
          <Content>
            <TradingPanelComponent
              id={id}
              payload={{ ...payload, selectedTradingAccount }}
              userTradeLimits={userTradeLimits}
              defaultTradeSizes={defaultTradeSizes}
            />
          </Content>
        )}
      </TradingProvider>
    </Widget>
  );
};
